/**
 * MapaCampoEditor.jsx
 * Editor digital do mapa de campo com mapa real de fundo (Mapbox).
 *
 * Rua principal: a rua onde corre a rede de esgoto.
 * Singularidades: o técnico toca no mapa para posicionar cada poço de visita.
 * Conexões: criadas automaticamente em sequência (principal) ou manualmente (ramal).
 * Ruas transversais: nomeadas por singularidade no painel lateral.
 *
 * Props:
 *   value    → { ruaPrincipal, nos, arestas, viewport }
 *   onChange → (novoValor) => void
 *   initLat        → latitude inicial (GPS capturado no bloco de endereço)
 *   initLng        → longitude inicial
 *   singularidades → array de singularidades do formulário (para importar PVs)
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import Map, { Marker, Source, Layer } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  Box, Flex, Text, Button, IconButton, Icon,
  Input, Stack, Badge, Divider,
  Tabs, TabList, TabPanels, Tab, TabPanel,
  useColorModeValue, Tooltip,
} from '@chakra-ui/react';
import {
  MdAdd, MdDelete, MdMap, MdEdit, MdArrowForward,
  MdMyLocation, MdAltRoute, MdLayers, MdClose,
} from 'react-icons/md';
import { MAPBOX_TOKEN } from 'utils/constants';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

/** Haversine — distância em metros entre dois pontos */
function haversine(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const d2r = (d) => (d * Math.PI) / 180;
  const dLat = d2r(lat2 - lat1);
  const dLng = d2r(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(d2r(lat1)) * Math.cos(d2r(lat2)) * Math.sin(dLng / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(a)));
}

export const MAPA_PADRAO = () => ({
  ruaPrincipal: '',
  nos: [],
  arestas: [],
  viewport: { longitude: -46.633, latitude: -23.55, zoom: 16 },
});

// ─── SVG Diagrama Esquemático ──────────────────────────────────────────────────
const PV_R     = 16;
const SEG_W    = 120;
const MX       = 48;
const MAIN_Y   = 100;
const BRANCH_Y = 195;

function calcLayout(nos, arestas) {
  const pos = {};
  if (!nos.length) return pos;
  const destsPrincipal = new Set(
    arestas.filter((a) => a.tipo === 'principal').map((a) => a.para),
  );
  const root = nos.find((n) => !destsPrincipal.has(n._id)) ?? nos[0];
  const visited = new Set();
  const queue = [{ id: root._id, x: MX, y: MAIN_Y }];
  while (queue.length) {
    const { id, x, y } = queue.shift();
    if (visited.has(id)) continue;
    visited.add(id);
    pos[id] = { x, y };
    for (const ar of arestas.filter((a) => a.de === id)) {
      if (visited.has(ar.para)) continue;
      const w = Math.max(SEG_W, (parseFloat(ar.metros) || 0) * 0.9);
      queue.push({
        id: ar.para,
        x: ar.tipo === 'ramal' ? x : x + w,
        y: ar.tipo === 'ramal' ? BRANCH_Y : y,
      });
    }
  }
  let xFree = (Object.keys(pos).length) * SEG_W + MX;
  for (const n of nos) {
    if (!pos[n._id]) { pos[n._id] = { x: xFree, y: MAIN_Y }; xFree += SEG_W; }
  }
  return pos;
}

function DiagramaSVG({ nos, arestas, ruaPrincipal }) {
  const pos     = calcLayout(nos, arestas);
  const xs      = Object.values(pos).map((p) => p.x);
  const totalW  = (xs.length ? Math.max(...xs) : MX) + MX + PV_R + 10;
  const hasRamal = arestas.some((a) => a.tipo === 'ramal');
  const totalH  = (hasRamal ? BRANCH_Y : MAIN_Y) + 70;
  const blue    = '#3965FF';
  const red     = '#E53E3E';
  const dark    = '#1B254B';
  const gray    = '#718096';

  return (
    <svg width={totalW} height={totalH} style={{ display: 'block', fontFamily: 'Arial,sans-serif' }}>
      {ruaPrincipal && (
        <text x={totalW / 2} y={MAIN_Y + PV_R + 28}
          textAnchor="middle" fill={blue} fontSize="11" fontWeight="800" letterSpacing="0.5">
          {ruaPrincipal.toUpperCase()}
        </text>
      )}
      {arestas.map((ar) => {
        const from = pos[ar.de]; const to = pos[ar.para];
        if (!from || !to) return null;
        const isPrinc = ar.tipo === 'principal';
        const cor = isPrinc ? blue : red;
        const dx = to.x - from.x; const dy = to.y - from.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const ux = dx / dist; const uy = dy / dist;
        const x1 = from.x + ux * PV_R; const y1 = from.y + uy * PV_R;
        const x2 = to.x - ux * PV_R;   const y2 = to.y - uy * PV_R;
        const mx = (x1 + x2) / 2;       const my = (y1 + y2) / 2;
        const sz = 6; const nx2 = -uy; const ny2 = ux;
        return (
          <g key={ar._id}>
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={cor}
              strokeWidth={isPrinc ? 2.5 : 2} strokeDasharray={isPrinc ? 'none' : '6,3'} />
            <polygon fill={cor}
              points={`${mx + ux * sz},${my + uy * sz} ${mx - ux * sz - nx2 * sz * 0.6},${my - uy * sz - ny2 * sz * 0.6} ${mx - ux * sz + nx2 * sz * 0.6},${my - uy * sz + ny2 * sz * 0.6}`} />
            {ar.metros && (
              <text x={mx} y={from.y === to.y ? from.y - 8 : my - 8}
                textAnchor="middle" fill={dark} fontSize="11" fontWeight="700">
                {ar.metros}m
              </text>
            )}
          </g>
        );
      })}
      {nos.map((n) => {
        const p = pos[n._id]; if (!p) return null;
        const hasRua = n.rua?.trim();
        return (
          <g key={n._id}>
            {hasRua && (
              <>
                <line x1={p.x} y1={p.y - PV_R - 2} x2={p.x} y2={36}
                  stroke={gray} strokeWidth={1.5} strokeDasharray="4,3" />
                <text x={p.x} y={30} textAnchor="end" fill="#4A5568"
                  fontSize="10" fontWeight="700"
                  transform={`rotate(-55, ${p.x}, 30)`}>
                  {n.rua}
                </text>
              </>
            )}
            <circle cx={p.x} cy={p.y} r={PV_R} fill={blue} stroke="white" strokeWidth={2.5} />
            <text x={p.x} y={p.y + 1} textAnchor="middle" dominantBaseline="middle"
              fill="white" fontSize="11" fontWeight="800">{n.num}</text>
            {n.imovel && (
              <text x={p.x} y={p.y + PV_R + 13} textAnchor="middle" fill={gray} fontSize="10">
                Nº{n.imovel}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// ─── Marcador PIN do PV ───────────────────────────────────────────────────────
function PVMarker({ num, selected, onClick }) {
  const label = String(num ?? '');
  const fontSize = label.length >= 3 ? 8 : label.length === 2 ? 10 : 12;
  return (
    <div onClick={onClick} style={{ cursor: 'pointer', userSelect: 'none' }}>
      <svg width="30" height="37" viewBox="0 0 36 44" style={{ display: 'block' }}>
        <path d="M18 0 C8 0 0 8 0 18 C0 30 18 44 18 44 C18 44 36 30 36 18 C36 8 28 0 18 0Z"
          fill={selected ? '#E53E3E' : '#3965FF'} stroke="white" strokeWidth="2.5" />
        <circle cx="18" cy="18" r="12" fill="white" opacity="0.96" />
        <text x="18" y="18" textAnchor="middle" dominantBaseline="middle"
          fill={selected ? '#E53E3E' : '#3965FF'}
          fontSize={fontSize}
          fontWeight="900"
          paintOrder="stroke"
          stroke="white"
          strokeWidth="0.35"
          fontFamily="Arial,sans-serif">
          {label}
        </text>
      </svg>
    </div>
  );
}

// ─── GeoJSON das conexões para o Layer do Mapbox ──────────────────────────────
function buildGeoJSON(nos, arestas) {
  const nosMap = Object.fromEntries(nos.map((n) => [n._id, n]));
  return {
    type: 'FeatureCollection',
    features: arestas
      .filter((ar) => {
        const f = nosMap[ar.de]; const t = nosMap[ar.para];
        return f?.lng && f?.lat && t?.lng && t?.lat;
      })
      .map((ar) => {
        const f = nosMap[ar.de]; const t = nosMap[ar.para];
        return {
          type: 'Feature',
          properties: { tipo: ar.tipo, metros: ar.metros || '' },
          geometry: { type: 'LineString', coordinates: [[f.lng, f.lat], [t.lng, t.lat]] },
        };
      }),
  };
}

// ─── Componente Principal ─────────────────────────────────────────────────────
export default function MapaCampoEditor({ value, onChange, initLat, initLng, singularidades, ruaPrincipal: ruaPrincipalProp }) {
  const inputBg   = useColorModeValue('white', 'whiteAlpha.100');
  const cardBg    = useColorModeValue('gray.50', 'navy.900');
  const borderCol = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('navy.700', 'white');
  const subText   = useColorModeValue('gray.500', 'gray.400');
  const diagramBg = useColorModeValue('white', 'navy.900');

  const data = value || MAPA_PADRAO();
  const { nos = [], arestas = [] } = data;
  // ruaPrincipal é sempre a rua de endereço preenchida no bloco de Localização
  const ruaPrincipal = ruaPrincipalProp || data.ruaPrincipal || '';

  const [vp, setVp] = useState(() => ({
    longitude: initLng || data.viewport?.longitude || -46.633,
    latitude:  initLat || data.viewport?.latitude  || -23.55,
    zoom:      data.viewport?.zoom || 16,
  }));

  useEffect(() => {
    if (initLat && initLng) setVp((v) => ({ ...v, latitude: initLat, longitude: initLng }));
  }, [initLat, initLng]);

  const [addMode,    setAddMode]    = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [mapStyle,   setMapStyle]   = useState('streets');

  const lastSingsKey = useRef(null);

  // ── Sincroniza nos/arestas com singularidades automaticamente ───────────────────
  useEffect(() => {
    const sings = singularidades || [];
    const key = JSON.stringify(sings.map(s => ({
      n: s.numeroImovel || '',
      m: s.metragemAoPV || '',
      p: s.pvAnteriorId || '',
      r: JSON.stringify((s.ramaisAdicionais || []).map(r => ({
        id: r._id, para: r.paraPVId, met: r.metragem, desc: r.descricao,
      }))),
    }))) + String(sings.length);
    if (key === lastSingsKey.current) return;
    lastSingsKey.current = key;
    if (!sings.length) return;

    const existingNos    = value?.nos     || [];
    const existingArestas = value?.arestas || [];
    const ruaPrinc       = value?.ruaPrincipal || '';

    const newNos = sings.map((s, i) => ({
      _id:    existingNos[i]?._id  ?? uid(),
      num:    String(i + 1),
      lat:    existingNos[i]?.lat  ?? null,
      lng:    existingNos[i]?.lng  ?? null,
      rua:    existingNos[i]?.rua  ?? ruaPrinc,
      imovel: s.numeroImovel || '',
    }));

    const getPrevIndex = (idx) => {
      if (idx <= 0) return -1;
      const current = sings[idx] || {};
      if (current.pvAnteriorId) {
        const found = sings.findIndex((s, sIdx) => sIdx < idx && String(s?._id) === String(current.pvAnteriorId));
        if (found >= 0) return found;
      }
      return idx - 1;
    };

    const newArestas = newNos.slice(1).map((n, i) => {
      const currentIdx = i + 1;
      const prevIdx = getPrevIndex(currentIdx);
      const fromNo = prevIdx >= 0 ? newNos[prevIdx] : newNos[i];
      const ex = existingArestas.find(a => a.de === fromNo._id && a.para === n._id);
      return {
        _id:    ex?._id ?? uid(),
        de:     fromNo._id,
        para:   n._id,
        metros: sings[currentIdx]?.metragemAoPV || ex?.metros || '',
        tipo:   prevIdx === currentIdx - 1 ? 'principal' : 'ramal',
      };
    });

    // Adiciona arestas dos ramaisAdicionais
    sings.forEach((sing, sIdx) => {
      const fromNoId = newNos[sIdx]?._id;
      if (!fromNoId || !sing.ramaisAdicionais?.length) return;

      sing.ramaisAdicionais.forEach((ramal) => {
        const fromSingIdx = sings.findIndex(s => s._id === ramal.paraPVId);  // origem
        const fromRamalNoId = fromSingIdx >= 0 ? newNos[fromSingIdx]?._id : null;
        const toNoId = newNos[sIdx]?._id;  // destino = sing atual
        if (!fromRamalNoId || !toNoId) return;

        const ex = existingArestas.find(a => a.de === fromRamalNoId && a.para === toNoId && a._ramalId === ramal._id);
        newArestas.push({
          _id:       ex?._id ?? uid(),
          _ramalId:  ramal._id,
          de:        fromRamalNoId,
          para:      toNoId,
          metros:    ramal.metragem || '',
          descricao: ramal.descricao || '',
          tipo:      'ramal',
        });
      });
    });

    onChange({ ...(value || MAPA_PADRAO()), nos: newNos, arestas: newArestas });
  }, [singularidades]); // eslint-disable-line react-hooks/exhaustive-deps

  const inputH  = { base: '44px', md: '36px' };
  const inputFs = { base: '16px', md: '14px' };

  const set = useCallback(
    (patch) => onChange({ ...data, ...patch }),
    [data, onChange],
  );

  const handleMapClick = useCallback((e) => {
    if (!addMode) return;
    const { lng, lat } = e.lngLat;
    const idx = nos.findIndex(n => !n.lat || !n.lng);
    if (idx === -1) { setAddMode(false); return; }
    const targetId = nos[idx]._id;
    const newNos   = nos.map((n, i) => i === idx ? { ...n, lat, lng } : n);
    let newArestas = arestas;
    if (idx > 0 && nos[idx - 1].lat && nos[idx - 1].lng) {
      newArestas = arestas.map(ar => {
        if (ar.para !== targetId) return ar;
        // Preserva metros já preenchido nas singularidades — só calcula se vazio
        if (ar.metros) return ar;
        const dist = String(haversine(nos[idx - 1].lat, nos[idx - 1].lng, lat, lng));
        return { ...ar, metros: dist };
      });
    }
    set({ nos: newNos, arestas: newArestas });
    setSelectedId(targetId);
    if (newNos.filter(n => !n.lat || !n.lng).length === 0) setAddMode(false);
  }, [addMode, nos, arestas, set]);

  const updateNo      = (id, f, v) => set({ nos: nos.map((n) => n._id === id ? { ...n, [f]: v } : n) });
  const updateAresta  = (id, m)    => set({ arestas: arestas.map((a) => a._id === id ? { ...a, metros: m } : a) });
  const limparPosicao = (id)       => set({ nos: nos.map((n) => n._id === id ? { ...n, lat: null, lng: null } : n) });

  const centralizarGPS = () =>
    navigator.geolocation?.getCurrentPosition((pos) =>
      setVp((v) => ({ ...v, latitude: pos.coords.latitude, longitude: pos.coords.longitude })));

  const nextUnpositioned = nos.find(n => !n.lat || !n.lng);
  const allPositioned    = nos.length > 0 && !nextUnpositioned;
  const selectedNo       = nos.find((n) => n._id === selectedId);
  const geoJSON          = buildGeoJSON(nos, arestas);
  const mapStyleUrl = mapStyle === 'satellite'
    ? 'mapbox://styles/mapbox/satellite-streets-v12'
    : 'mapbox://styles/mapbox/streets-v12';

  return (
    <Tabs variant="enclosed" colorScheme="brand" isLazy>
      <TabList mb="0">
        <Tab fontSize="xs" fontWeight="700"><Icon as={MdMap} me="6px" />Mapa</Tab>
        <Tab fontSize="xs" fontWeight="700"><Icon as={MdEdit} me="6px" />Editar SINGs</Tab>
      </TabList>

      <TabPanels>

        {/* ══ ABA MAPA ═══════════════════════════════════════════════════════ */}
        <TabPanel px="0" pt="12px">
          <Stack spacing="10px">

            {/* Rua da rede — somente leitura, preenchida na Localização do Trecho */}
            {ruaPrincipal ? (
              <Flex align="center" gap="8px" p="8px" borderRadius="8px"
                bg={cardBg} border="1px solid" borderColor={borderCol}>
                <Text fontSize="11px" fontWeight="700" textTransform="uppercase"
                  letterSpacing=".05em" color={subText} flexShrink="0">Rua da rede:</Text>
                <Badge colorScheme="blue" borderRadius="full" px="3" fontSize="12px"
                  fontWeight="700">
                  {ruaPrincipal}
                </Badge>
              </Flex>
            ) : (
              <Text fontSize="12px" color="orange.500" fontWeight="600"
                p="8px" borderRadius="8px" bg="orange.50">
                Preencha o endereço na seção “Localização do Trecho” acima.
              </Text>
            )}

            {/* Controles */}
            <Flex gap="8px" align="center" justify="space-between" wrap="wrap">
              <Flex gap="8px" align="center" flex="1" wrap="wrap">
                {nos.length === 0 ? (
                  <Text fontSize="12px" color={subText}>
                    Preencha as singularidades — as SINGs aparecerão automaticamente.
                  </Text>
                ) : allPositioned ? (
                  <Badge colorScheme="green" borderRadius="full" px="3" py="1" fontSize="12px">
                    ✓ {nos.length} SINGs posicionadas
                  </Badge>
                ) : (
                  <>
                    <Button size="sm" borderRadius="8px"
                      colorScheme={addMode ? 'red' : 'brand'}
                      leftIcon={<Icon as={addMode ? MdClose : MdAdd} />}
                      onClick={() => setAddMode((v) => !v)}>
                      {addMode ? 'Cancelar' : 'Posicionar SINGs'}
                    </Button>
                    <Badge colorScheme="orange" borderRadius="full" px="2" fontSize="12px">
                      {nos.filter(n => n.lat && n.lng).length}/{nos.length} posicionadas
                    </Badge>
                  </>
                )}
              </Flex>
              <Flex gap="6px" flexShrink="0">
                <Tooltip label="Centralizar no meu GPS">
                  <IconButton icon={<Icon as={MdMyLocation} />} size="sm"
                    borderRadius="8px" variant="outline" aria-label="GPS" onClick={centralizarGPS} />
                </Tooltip>
                <Tooltip label="Alternar satélite / ruas">
                  <IconButton icon={<Icon as={MdLayers} />} size="sm"
                    borderRadius="8px" variant="outline" aria-label="Estilo"
                    onClick={() => setMapStyle((s) => s === 'streets' ? 'satellite' : 'streets')} />
                </Tooltip>
              </Flex>
            </Flex>

            {/* MAPA */}
            <Box position="relative" borderRadius="14px" overflow="hidden"
              h={{ base: '450px', md: '520px' }}
              border="2px solid" borderColor={addMode ? 'brand.400' : borderCol}>
              <Map
                {...vp}
                onMove={(e) => setVp(e.viewState)}
                onClick={handleMapClick}
                mapStyle={mapStyleUrl}
                mapboxAccessToken={MAPBOX_TOKEN}
                style={{ width: '100%', height: '100%' }}
                cursor={addMode ? 'crosshair' : 'grab'}>

                {geoJSON.features.length > 0 && (
                  <Source id="rede" type="geojson" data={geoJSON}>
                    <Layer id="rede-princ" type="line"
                      paint={{ 'line-color': '#3965FF', 'line-width': 4, 'line-opacity': 0.95 }} />
                    <Layer id="rede-labels" type="symbol"
                      filter={['!=', ['get', 'metros'], '']}
                      layout={{
                        'symbol-placement': 'line-center',
                        'text-field': ['concat', ['get', 'metros'], 'm'],
                        'text-size': 12,
                        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                        'text-offset': [0, -0.9],
                        'text-allow-overlap': false,
                      }}
                      paint={{
                        'text-color': '#1B254B',
                        'text-halo-color': 'rgba(255,255,255,0.92)',
                        'text-halo-width': 2,
                      }} />
                  </Source>
                )}

                {nos.map((n) => n.lat && n.lng ? (
                  <Marker key={n._id} longitude={n.lng} latitude={n.lat} anchor="bottom"
                    draggable
                    onDragEnd={(e) => {
                      const { lng: newLng, lat: newLat } = e.lngLat;
                      set({ nos: nos.map(m => m._id === n._id ? { ...m, lat: newLat, lng: newLng } : m) });
                    }}>
                    <PVMarker num={n.num} selected={selectedId === n._id}
                      onClick={() => {
                        setSelectedId(n._id === selectedId ? null : n._id);
                        setAddMode(false);
                      }} />
                  </Marker>
                ) : null)}
              </Map>

              {/* Overlay: instrução de posicionamento */}
              {addMode && nextUnpositioned && (
                <Flex position="absolute" bottom="10px" left="10px" right="10px"
                  bg="rgba(25,30,60,0.82)" backdropFilter="blur(6px)"
                  borderRadius="12px" px="14px" py="10px"
                  align="center" justify="space-between" gap="8px" pointerEvents="all">
                  <Text color="white" fontSize="13px" fontWeight="700" flex="1">
                    📍 Toque no mapa — SING {nextUnpositioned.num}
                    {nextUnpositioned.imovel ? ` (Imóvel ${nextUnpositioned.imovel})` : ''}
                  </Text>
                  <IconButton icon={<Icon as={MdClose} />} size="sm"
                    variant="ghost" aria-label="Cancelar" color="white"
                    _hover={{ bg: 'whiteAlpha.300' }}
                    onClick={() => setAddMode(false)} />
                </Flex>
              )}

              {/* Overlay: painel da SING selecionada */}
              {selectedNo && !addMode && (
                <Box position="absolute" bottom="10px" left="10px" right="10px"
                  bg={cardBg} borderRadius="12px" p="12px"
                  boxShadow="0 4px 24px rgba(0,0,0,0.28)"
                  border="1px solid" borderColor="brand.300" pointerEvents="all">
                  <Flex justify="space-between" align="center" mb="8px">
                    <Badge colorScheme="blue" borderRadius="full" px="3" fontSize="12px">
                      SING {selectedNo.num}{selectedNo.imovel ? ` — Imóvel ${selectedNo.imovel}` : ''}
                    </Badge>
                    <Flex gap="4px">
                      <Tooltip label="Reposicionar (ou arraste o marcador)">
                        <IconButton icon={<Icon as={MdMyLocation} />} size="xs"
                          colorScheme="orange" variant="ghost" aria-label="Reposicionar"
                          onClick={() => { limparPosicao(selectedNo._id); setSelectedId(null); setAddMode(true); }} />
                      </Tooltip>
                      <IconButton icon={<Icon as={MdClose} />} size="xs"
                        colorScheme="gray" variant="ghost" aria-label="Fechar"
                        onClick={() => setSelectedId(null)} />
                    </Flex>
                  </Flex>
                  <Text fontSize="10px" color={subText} mb="4px">Rua transversal (cruzamento)</Text>
                  <Input value={selectedNo.rua || ''} placeholder="Ex: Rua das Flores"
                    bg={inputBg} borderRadius="8px" h="44px" fontSize="15px"
                    onChange={(e) => updateNo(selectedNo._id, 'rua', e.target.value)} />
                </Box>
              )}
            </Box>

            {nos.some(n => n.lat && n.lng) && (
              <Text fontSize="11px" color={subText} textAlign="center">
                Arraste um marcador para reposicionar • Toque para editar
              </Text>
            )}
          </Stack>
        </TabPanel>

        {/* ══ ABA EDITAR PVs ═════════════════════════════════════════════════ */}
        <TabPanel px="0" pt="12px">
          <Stack spacing="10px">
            {ruaPrincipal && (
              <Flex align="center" gap="8px" p="8px" borderRadius="8px"
                bg={cardBg} border="1px solid" borderColor={borderCol}>
                <Text fontSize="11px" fontWeight="700" textTransform="uppercase"
                  letterSpacing=".05em" color={subText} flexShrink="0">Rua da rede:</Text>
                <Badge colorScheme="blue" borderRadius="full" px="3" fontSize="12px"
                  fontWeight="700">
                  {ruaPrincipal}
                </Badge>
              </Flex>
            )}
            <Divider />
            <Text fontSize="11px" fontWeight="800" textTransform="uppercase"
              letterSpacing=".06em" color={textColor}>Singularidades (sincronizadas)</Text>
            {nos.length === 0 && (
              <Text fontSize="13px" color={subText} textAlign="center" py="2">
                Preencha as singularidades acima para as SINGs aparecerem aqui.
              </Text>
            )}
            <Stack spacing="8px">
              {nos.map((n) => (
                <Box key={n._id} p="10px" bg={cardBg} borderRadius="10px"
                  border="1px solid" borderColor={borderCol}>
                  <Flex gap="8px" align="center" wrap="wrap">
                    <Badge colorScheme={n.lat && n.lng ? 'green' : 'gray'}
                      borderRadius="full" px="3" fontSize="12px" flexShrink="0">
                      SING {n.num}{n.imovel ? ` — Nº ${n.imovel}` : ''}
                    </Badge>
                    <Box flex="1" minW="110px">
                      <Text fontSize="10px" color={subText} mb="2px">Rua transversal</Text>
                      <Input value={n.rua || ''} placeholder="opcional"
                        bg={inputBg} borderRadius="8px" h={inputH} fontSize={inputFs}
                        onChange={(e) => updateNo(n._id, 'rua', e.target.value)} />
                    </Box>
                    <Text fontSize="10px" color={n.lat && n.lng ? 'green.500' : subText} flexShrink="0">
                      {n.lat && n.lng ? '✓ Posicionado' : '○ Sem posição'}
                    </Text>
                  </Flex>
                </Box>
              ))}
            </Stack>
            <Divider />
            <Text fontSize="11px" fontWeight="800" textTransform="uppercase"
              letterSpacing=".06em" color={textColor}>Distâncias entre SINGs</Text>
            {arestas.length === 0 && (
              <Text fontSize="13px" color={subText} textAlign="center" py="1">
                Distâncias são preenchidas com metragem ao anterior das singularidades e refinadas pelo GPS.
              </Text>
            )}
            <Stack spacing="8px">
              {arestas.map((ar) => {
                const fromNo = nos.find((n) => n._id === ar.de);
                const toNo   = nos.find((n) => n._id === ar.para);
                return (
                  <Flex key={ar._id} gap="8px" align="center" p="10px"
                    bg={cardBg} borderRadius="10px"
                    border="1px solid" borderColor={borderCol} wrap="wrap">
                    <Text fontSize="13px" fontWeight="700" color={textColor} flex="1">
                      SING {fromNo?.num ?? '?'}
                      <Icon as={MdArrowForward} mx="4px" boxSize="14px" />
                      SING {toNo?.num ?? '?'}
                    </Text>
                    <Badge colorScheme="gray" borderRadius="6px" px="3" fontSize="12px" flexShrink="0">
                      {ar.metros ? `${ar.metros} m` : '— m'}
                    </Badge>
                  </Flex>
                );
              })}
            </Stack>
          </Stack>
        </TabPanel>

      </TabPanels>
    </Tabs>
  );
}
